import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Plus,
  Eye,
  AlertCircle,
  Ticket,
  MessageSquare,
  Activity,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";

interface DashboardStats {
  totalTemplates: number;
  openErrors: number;
  openTickets: number;
  stewSessionsThisWeek: number;
}

interface AuditActivity {
  id: string;
  action_type: string;
  resource_type: string;
  created_at: string;
  admin_user_id: string;
}

interface OpenTicket {
  id: string;
  subject: string | null;
  created_at: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    totalTemplates: 0,
    openErrors: 0,
    openTickets: 0,
    stewSessionsThisWeek: 0,
  });
  const [recentActivity, setRecentActivity] = useState<AuditActivity[]>([]);
  const [openTickets, setOpenTickets] = useState<OpenTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadDashboardData() {
    try {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [templatesRes, errorsRes, ticketsRes, stewRes] = await Promise.all([
        supabase
          .from("document_templates")
          .select("id", { count: "exact", head: true })
          .eq("status", "published"),
        supabase
          .from("error_events")
          .select("id", { count: "exact", head: true })
          .in("status", ["new", "investigating"]),
        supabase
          .from("support_tickets")
          .select("id", { count: "exact", head: true })
          .eq("status", "open"),
        supabase
          .from("stew_conversations")
          .select("id", { count: "exact", head: true })
          .gte("updated_at", weekAgo),
      ]);

      setStats({
        totalTemplates: templatesRes.count || 0,
        openErrors: errorsRes.count || 0,
        openTickets: ticketsRes.count || 0,
        stewSessionsThisWeek: stewRes.count || 0,
      });

      const { data: activities } = await supabase
        .from("admin_audit_log")
        .select("id, action_type, resource_type, created_at, admin_user_id")
        .order("created_at", { ascending: false })
        .limit(10);
      setRecentActivity((activities as AuditActivity[]) || []);

      const { data: tickets } = await supabase
        .from("support_tickets")
        .select("id, subject, created_at")
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(5);
      setOpenTickets((tickets as OpenTicket[]) || []);
    } catch (error: any) {
      console.error("Error loading dashboard:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const getActionIcon = (action: string) => {
    if (action.includes("create")) return <Plus className="h-4 w-4" />;
    if (action.includes("update")) return <Activity className="h-4 w-4" />;
    if (action.includes("approve")) return <CheckCircle className="h-4 w-4" />;
    if (action.includes("delete")) return <AlertCircle className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Live signals across errors, support, and Stew
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={() => navigate("/admin/templates")} className="min-h-[44px]">
            <Eye className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">View Templates</span>
            <span className="sm:hidden">Templates</span>
          </Button>
          <Button onClick={() => navigate("/admin/templates/new")} className="min-h-[44px]">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Template</span>
            <span className="sm:hidden">Create</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Active Templates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "-" : stats.totalTemplates}</div>
            <p className="text-xs text-muted-foreground">Published and ready to use</p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer hover:bg-accent ${stats.openErrors > 0 ? "border-destructive" : ""}`}
          onClick={() => navigate("/admin/system/errors")}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Open Errors</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "-" : stats.openErrors}</div>
            <p className="text-xs text-muted-foreground">Unresolved fingerprints</p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:bg-accent"
          onClick={() => navigate("/admin/support/tickets")}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "-" : stats.openTickets}</div>
            <p className="text-xs text-muted-foreground">Waiting on a reply</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Stew Sessions</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "-" : stats.stewSessionsThisWeek}</div>
            <p className="text-xs text-muted-foreground">Active in the last seven days</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Admin Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 text-sm">
                    <div className="mt-0.5 text-muted-foreground">
                      {getActionIcon(activity.action_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {activity.action_type.replace(/_/g, " ")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.resource_type} • {format(new Date(activity.created_at), "PPp")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Open Support Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {openTickets.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No open tickets</p>
              ) : (
                openTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex items-start gap-3 border-b pb-3 last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {ticket.subject || "Untitled ticket"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(ticket.created_at), "PP")}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                    >
                      Open
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
